import { Injectable } from '@angular/core'
import { Observable, forkJoin } from 'rxjs'
import { map } from 'rxjs/operators'
import { ApiService } from './api.service'
import { AccountService } from '../account/services/account.service'
import { AccessLevelService } from './access-level.service'
import { RoleDto, ApplicationDto } from './dtos'

export interface AccessLevelInfo {
  accessLevel: string
  applications: string[]
}

export interface RoleItem {
  id: string
  name: string
  accessLevels: AccessLevelInfo[]
  selected: boolean
  isSystemRole?: boolean
}

@Injectable({
  providedIn: 'root'
})
export class RoleLoadingService {
  private rolesCache: RoleItem[] | null = null

  constructor(
    private apiService: ApiService,
    private accountService: AccountService,
    private accessLevelService: AccessLevelService
  ) {}

  /**
   * Load and format roles with their access levels and applications
   * @param useCache Whether to use cached roles if available
   * @returns Promise resolving to formatted RoleItem array
   */
  async loadRoles(useCache: boolean = false): Promise<RoleItem[]> {
    // Return cached data if available and requested
    if (useCache && this.rolesCache) {
      return this.rolesCache.map((role) => ({ ...role, selected: false }))
    }

    try {
      // Load both roles and applications in parallel
      const [roleDtos, applications] = await Promise.all([
        this.apiService.listRoles(this.accountService.accountId).toPromise(),
        this.apiService.listApplications(this.accountService.accountId).toPromise(),
      ])

      const roles = roleDtos.map((roleDto) => this.convertRoleDto(roleDto, applications))

      // Sort roles: system roles first, then alphabetically
      roles.sort((a, b) => {
        const aIsDefault = this.accessLevelService.isDefaultAccountRole(a)
        const bIsDefault = this.accessLevelService.isDefaultAccountRole(b)

        if (aIsDefault && !bIsDefault) return -1
        if (!aIsDefault && bIsDefault) return 1
        return a.name.localeCompare(b.name)
      })

      // Cache the roles for future use
      this.rolesCache = [...roles]

      return roles
    } catch (error) {
      console.error('Failed to load roles:', error)
      return []
    }
  }

  /**
   * Clear the roles cache
   */
  clearCache(): void {
    this.rolesCache = null
  }

  /**
   * Convert a RoleDto to a RoleItem with calculated access levels
   */
  private convertRoleDto(roleDto: RoleDto, applications: ApplicationDto[]): RoleItem {
    // Calculate all unique access levels with their associated applications
    const accessLevelMap = new Map<string, Set<string>>()

    roleDto.policies?.forEach((policy) => {
      if (!accessLevelMap.has(policy.accessLevel)) {
        accessLevelMap.set(policy.accessLevel, new Set<string>())
      }

      if (policy.allApps) {
        // Add all applications
        applications.forEach((app) => accessLevelMap.get(policy.accessLevel)!.add(app.name))
      } else {
        // Add specific applications
        policy.appIds?.forEach((appId) => {
          const app = applications.find((a) => a.appId === appId)
          if (app) {
            accessLevelMap.get(policy.accessLevel)!.add(app.name)
          }
        })
      }
    })

    // Convert to AccessLevelInfo array
    const accessLevelInfos: AccessLevelInfo[] = Array.from(accessLevelMap.entries()).map(([level, apps]) => ({
      accessLevel: level,
      applications: Array.from(apps).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
    }))

    return {
      id: roleDto.roleId || '',
      name: roleDto.name || '',
      accessLevels: accessLevelInfos,
      selected: false,
      isSystemRole: roleDto.isSystemRole || false,
    }
  }
}